import { ALIGN_CLASS, headingAlign, headingLabel, type Heading } from './headings';
import type { ReactNode } from 'react';
import { ApiError } from '../../api/client';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="no-print mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {/* Kept under 80 characters a line: this is guidance, not a paragraph. */}
        {subtitle ? <p className="mt-0.5 max-w-2xl text-sm text-ink-soft">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Errors carry a code, a sentence and sometimes a request id. All three are
 * shown: the sentence for the person, the id for whoever they ring.
 */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const problem = error instanceof ApiError ? error.problem : null;
  const detail = problem?.detail ?? (error instanceof Error ? error.message : 'Something went wrong.');

  return (
    <div className="mb-4 rounded-md border-l-2 border-danger bg-danger/5 px-3 py-2.5 text-sm">
      <p className="text-danger">{detail}</p>
      {problem?.errors?.length ? (
        <ul className="mt-1.5 space-y-0.5 text-ink-soft">
          {problem.errors.map((e) => (
            <li key={e.field}>
              <span className="font-medium">{e.field}</span> — {e.message}
            </li>
          ))}
        </ul>
      ) : null}
      {problem?.requestId ? (
        <p className="mt-1.5 text-2xs text-ink-faint">
          Reference <span className="num">{problem.requestId}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Loading looks like the thing that is coming.
 *
 * A row of grey bars where the table will be tells somebody the page is working
 * and roughly what to expect; the word "Loading…" tells them neither.
 */
export function Loading({ what = 'Loading', rows = 4 }: { what?: string; rows?: number }) {
  return (
    <div className="overflow-hidden rounded-md border border-silver-100 bg-surface" role="status" aria-label={what}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-silver-100/70 px-3 py-3 last:border-0">
          <div className="h-2.5 w-1/5 rounded bg-silver-100" />
          <div className="h-2.5 w-2/5 rounded bg-silver-50" />
          <div className="ml-auto h-2.5 w-16 rounded bg-silver-100" />
        </div>
      ))}
    </div>
  );
}

/**
 * An empty screen is an invitation to act, not a dead end.
 *
 * The old one was a dotted box saying "No purchases recorded yet." with the
 * button that fixes it in the far corner of the page. This puts the next step
 * where the person is already looking.
 */
export function Empty({
  message,
  hint,
  action,
}: {
  message: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="rounded-md border border-silver-100 bg-surface px-6 py-12 text-center">
      <p className="text-sm text-ink">{message}</p>
      {hint ? <p className="mx-auto mt-1 max-w-md text-sm text-ink-soft">{hint}</p> : null}
      {action ? (
        <button className="btn-primary mt-4" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

/**
 * A table that stays readable when it is long: the header sticks, rows separate
 * with a hairline rather than a box, and the whole thing scrolls sideways on a
 * narrow screen instead of squashing the figures.
 */
export function Table({ head, children }: { head: Heading[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-silver-100 bg-surface shadow-card">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} className={`th ${ALIGN_CLASS[headingAlign(h)]}`}>
                {headingLabel(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/**
 * One figure with its name.
 *
 * The number is the loud part and the label is quiet, because somebody glancing
 * at a shelf of these is looking for the number.
 */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'warn' | 'bad';
}) {
  const colour = tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : '';
  return (
    <div className="rounded-md border border-silver-100 bg-surface px-3.5 py-3 shadow-card">
      <p className="text-2xs text-ink-soft">{label}</p>
      <p className={`num mt-1 text-xl font-semibold tracking-tight ${colour}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-2xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}

/** A short status word, coloured by what it means rather than decorated. */
// Written out in full so the stylesheet build sees every one; a class put
// together as `chip-${tone}` was dropped from the CSS unless some other screen
// happened to spell it out.
const CHIP_CLASS = { good: 'chip-good', warn: 'chip-warn', bad: 'chip-bad', mute: 'chip-mute' } as const;

export function Chip({ tone, children }: { tone: 'good' | 'warn' | 'bad' | 'mute'; children: ReactNode }) {
  return <span className={CHIP_CLASS[tone]}>{children}</span>;
}
